/**
 * The app's implementation of `@repo/client/api`'s `TokenStore` port.
 *
 * Secure storage on the device, `localStorage` on the web (both through
 * `secureStorage`). The pair is one JSON value under one key, written and read
 * through the package's own `serializeTokenPair`/`parseTokenPair`, so this
 * cannot drift from the memory double the client's tests run against.
 *
 * Every call is caught, as the port requires: a keystore that refuses — a key
 * invalidated by a lock-screen change, an encrypted value restored from a
 * backup onto a device that never held its key — reads as signed out and
 * never throws at boot.
 */
import {
  parseTokenPair,
  serializeTokenPair,
  type TokenStore,
} from "@repo/client/api";
import { secureStorage } from "./storage";

const SESSION_KEY = "session";

export function createTokenStore(): TokenStore {
  return {
    async clear() {
      try {
        await secureStorage.remove(SESSION_KEY);
      } catch (error) {
        console.warn("[native] token store clear failed", error);
      }
    },
    async read() {
      try {
        return parseTokenPair(await secureStorage.get(SESSION_KEY));
      } catch (error) {
        console.warn("[native] token store read failed", error);
        return null;
      }
    },
    async write(pair) {
      try {
        await secureStorage.set(SESSION_KEY, serializeTokenPair(pair));
      } catch (error) {
        // The session still lives in the ApiClient's memory for this run; it
        // just will not survive a restart. Degrading beats throwing mid-login.
        console.warn("[native] token store write failed", error);
      }
    },
  };
}
